import { GongFlowBrowse } from '../../_shared/components/flow';
import { useGongFlowBrowseController } from '../../_shared/hooks';
import type { GongFlowSummary } from '../../_shared/flow/gong';

export interface LauncherGongFlowBrowseProps {
  /** Fires immediately on a row click — see `GongFlowBrowseProps#onSelect`. */
  onSelect: (flow: GongFlowSummary) => void;
  /** The panel's own close control AND its Escape/outside-click handler. */
  onClose: () => void;
  /** The back control's visible label — `GongFlowBrowseProps#backLabel`. */
  backLabel: string;
}

/**
 * Launcher V2 round-3b QA fixes, G3 — a thin wrapper around Flow's REAL
 * "Gong Flows" browse panel (`GongFlowBrowse`, driven by
 * `useGongFlowBrowseController`), replacing the launcher's own
 * `GongFlowPickerInline` at both its mount points
 * (`play-flow/SequenceMappingDrawer.tsx`'s Gong tab,
 * `PlaybooksConfigPage.tsx#SequenceCreateFields`'s "New Sequence" form).
 * `GongFlowPickerInline` is NOT copied — Spencer's re-QA call was that the
 * launcher's inline picker isn't the UI he wants; this mounts the exact
 * component/hook pair Flow's own queue and contact-card pickers use.
 *
 * `contactLabel` (the queue's "Assigning {contact}" subtitle) is
 * deliberately omitted — there is no contact this panel is assigning a flow
 * FOR here, and `GongFlowBrowseProps#contactLabel` is optional precisely for
 * a mount site with nothing to name.
 *
 * `onPin`/`onHide` are NOT stripped out: they ride along with
 * `useGongFlowBrowseController()`'s spread, because this panel IS the real
 * Flow surface, pin/hide included — the "launcher amputates pin/hide"
 * history (`GongFlowPickerInline`'s own module doc, PR B follow-up U8) was
 * specific to the launcher's OWN inline picker; reusing Flow's panel
 * wholesale means reusing its pin/hide too, backed by the same real
 * `PUT /api/flow/gong/flows/:flowId/preference` mutation every other Flow
 * Gong surface already writes through.
 */
export function LauncherGongFlowBrowse({ onSelect, onClose, backLabel }: LauncherGongFlowBrowseProps) {
  const controller = useGongFlowBrowseController();
  return (
    <GongFlowBrowse
      {...controller}
      onSelect={onSelect}
      onClose={onClose}
      backLabel={backLabel}
      contactLabel={undefined}
    />
  );
}
